import React, { useRef, useEffect, useState } from "react";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import axios from "axios";
// Reusable field components
function UsageField({ value, onChange, name = "usage" }: { value: string; onChange: (e: React.ChangeEvent<any>) => void; name?: string }) {
  return (
    <div className="form-group">
      <label>Usage</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select</option>
        <option value={0}>Artisan Use</option>
        <option value={1}>Business Use</option>
        <option value={4}>Farm</option>
        <option value={7}>Pleasure</option>
        <option value={3}>Work School</option>
      </select>
    </div>
  );
}

function MilesToWorkField({
  value,
  onChange,
  name = "milesToWork",
}: {
  value: string | number;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>Miles To Work</label>
      <input
        type="number"
        name={name}
        className="form-input"
        value={value}
        onChange={onChange}
        min={0}
        max={999}
        step={1}
        placeholder="0–999"
        onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
      />
    </div>
  );
}

function AssignedDriverIdField({
  value,
  onChange,
  name = "assignedDriverId",
}: {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>Assigned Driver ID</label>
      <input type="text" name={name} className="form-input" value={value} onChange={onChange} placeholder="Enter assigned driver ID" />
    </div>
  );
}

function SafetyGlassCoverageField({
  value,
  onChange,
  name = "safetyGlassCoverage",
}: {
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  name?: string;
}) {
  return (
    <div className="form-group">
      <label>Safety Glass Coverage</label>
      <select name={name} className="form-input compact-select" value={value} onChange={onChange}>
        <option value="">Select</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );
}

const TELEMATICS_OPTIONS = ["Yes", "No"];
// Unified arrays below
const COMP_DEDUCTIBLE_UNIFIED = [
  "None", "0", "50", "100", "150", "200", "250", "300", "350", "400", "450", "475", "500", "550", "600", "700", "750", "800", "975", "1000", "1500", "2000", "2500", "3000", "5000"
];
const COLLISION_DEDUCTIBLE_UNIFIED = [
  "None", "0", "50", "100", "150", "200", "250", "300", "350", "400", "450", "475", "500", "550", "600", "700", "750", "800", "975", "1000", "1500", "2000", "2500", "3000", "5000"
];
const RENTAL_LIMIT_UNIFIED = ["None", "20", "25", "30", "35", "40", "50"];
const TOWING_LIMIT_UNIFIED = ["None", "20", "30", "35", "40", "50", "70", "75", "100"];
const GAP_OPTIONS = ["Yes", "No"];
const CUSTOM_EQUIPMENT_MIN = 0;
const CUSTOM_EQUIPMENT_MAX = 99999;
const SAFETY_GLASS_OPTIONS = ["Yes", "No"];
// Unified arrays complete. Removed stray lines.

export default function VehicleInformationConfig({
  vehicleConfigurationList,
  state,
  getVehicleConfigurationDetails,
  loading,
}: {
  vehicleConfigurationList: any;
  state: string;
  getVehicleConfigurationDetails: () => void;
  loading: boolean;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    annualMileage: "",
    telematics: "",
    comprehensiveDeductible: "",
    collisionDeductible: "",
    rentalLimit: "",
    towingAndLaborLimit: "",
    customEquipmentLimit: "",
    gap: "",
    usage: "",
    milesToWork: "",
    assignedDriverId: "",
    safetyGlassCoverage: "",
  });
  const toast = React.useRef<Toast>(null);
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);

  // Determine which comprehensive deductibles to use based on state
  const comprehensiveDeductibles = COMP_DEDUCTIBLE_UNIFIED;

  // Determine which collision deductibles to use based on state
  const collisionDeductibles = COLLISION_DEDUCTIBLE_UNIFIED;

  // Determine which rental limits to use based on state
  const rentalLimits = RENTAL_LIMIT_UNIFIED;

  // Determine which towing limits to use based on state
  const towingLimits = TOWING_LIMIT_UNIFIED;
  useEffect(() => {
    if (vehicleConfigurationList) {
      setFormData({
        annualMileage: vehicleConfigurationList.annualMileage ?? "",
        telematics: vehicleConfigurationList.telematics ?? "",
        comprehensiveDeductible: vehicleConfigurationList.comprehensiveDeductible ?? "",
        collisionDeductible: vehicleConfigurationList.collisionDeductible ?? "",
        rentalLimit: vehicleConfigurationList.rentalLimit ?? "",
        towingAndLaborLimit: vehicleConfigurationList.towingAndLaborLimit ?? "",
        customEquipmentLimit: vehicleConfigurationList.customEquipmentLimit ?? "",
        gap: vehicleConfigurationList.gap ?? "",
        usage: vehicleConfigurationList.usage ?? "",
        milesToWork: vehicleConfigurationList.milesToWork ?? "",
        assignedDriverId: vehicleConfigurationList.assignedDriverId ?? "",
        safetyGlassCoverage: vehicleConfigurationList.safetyGlassCoverage ?? "",
      });
    }
  }, [vehicleConfigurationList]);

  const handleReset = () => {
    setFormData({
      annualMileage: "",
      telematics: "",
      comprehensiveDeductible: "",
      collisionDeductible: "",
      rentalLimit: "",
      towingAndLaborLimit: "",
      customEquipmentLimit: "",
      gap: "",
      usage: "",
      milesToWork: "",
      assignedDriverId: "",
      safetyGlassCoverage: "",
    });
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let clamped: string | null = value === "" ? null : value;
    if (name === "milesToWork" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(999, Math.max(0, n)));
    } else if (name === "annualMileage" && value !== "") {
      const n = Math.floor(Number(value));
      clamped = String(Math.min(1000000, Math.max(0, n)));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: clamped,
    }));
  };
  const onVehicleConfigurationSaveClick = () => {
    if (!state || state.trim() === "") {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "State is required.",
        life: 3000,
      });
      return;
    }

    setSaveLoading(true);
    let data = JSON.stringify({
      annualMileage: formData.annualMileage,
      telematics: formData.telematics,
      comprehensiveDeductible: formData.comprehensiveDeductible,
      collisionDeductible: formData.collisionDeductible,
      rentalLimit: formData.rentalLimit,
      towingAndLaborLimit: formData.towingAndLaborLimit,
      customEquipmentLimit: formData.customEquipmentLimit,
      gap: formData.gap,
      usage: formData.usage,
      milesToWork: formData.milesToWork,
      assignedDriverId: formData.assignedDriverId,
      safetyGlassCoverage: formData.safetyGlassCoverage,
      state: state,
    });

    var config = {
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/vehicleinformationconfiguration`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then((response: any) => {
        setSaveLoading(false);
        getVehicleConfigurationDetails();
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Vehicle configurations saved successfully!",
          life: 3000,
        });
      })
      .catch((error: any) => {
        setSaveLoading(false);
        if (error.response && error.response.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while saving the vehicle information configurations!"}`
                : "Unknown error while saving the vehicle information configurations!",
            life: 3000,
          });
        }
      });
  };
  return (
    <div>
      <Toast ref={toast} />

      <div className="config-info-banner">
        <div className="config-info-icon">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <line x1="12" y1="8" x2="12" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="config-info-text">
          <p className="feature-bold">Vehicle Information Settings</p>
          <span className="feature-label">Configure vehicle-related defaults below.</span>
        </div>
      </div>
      <div style={{ position: "relative" }}>
        {loading && (
          <div className="driver-loading-overlay">
            <div className="driver-loading-content">
              <div className="driver-loading-spinner" />
              <p className="driver-loading-text">Loading vehicle configuration...</p>
            </div>
          </div>
        )}
        <form>
          <div
            className="form-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(220px, 1fr))",
              gap: "1rem",
            }}
          >
            <UsageField value={formData.usage} onChange={handleChange} />

            {/* Miles To Work */}
            <MilesToWorkField value={formData.milesToWork} onChange={handleChange} />

            {/* Annual Mileage */}
            <div className="form-group">
              <label>Annual Mileage</label>
              <input
                type="number"
                name="annualMileage"
                className="form-input"
                value={formData.annualMileage}
                onChange={handleChange}
                min={0}
                max={1000000}
                step={1}
                placeholder="0–1000000"
                onKeyDown={(e) => [".", "e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
              />
            </div>

            {/* Telematics */}
            <div className="form-group">
              <label>Telematics</label>
              <select name="telematics" className="form-input compact-select" value={formData.telematics} onChange={handleChange}>
                <option value="">Select</option>
                {TELEMATICS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Comprehensive Deductible */}
            <div className="form-group">
              <label>Comprehensive Deductible</label>
              <select
                name="comprehensiveDeductible"
                className="form-input compact-select"
                value={formData.comprehensiveDeductible}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {comprehensiveDeductibles.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Collision Deductible */}
            <div className="form-group">
              <label>Collision Deductible</label>
              <select name="collisionDeductible" className="form-input compact-select" value={formData.collisionDeductible} onChange={handleChange}>
                <option value="">Select</option>
                {collisionDeductibles.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Rental Limit */}
            <div className="form-group">
              <label>Rental Limit</label>
              <select name="rentalLimit" className="form-input compact-select" value={formData.rentalLimit} onChange={handleChange}>
                <option value="">Select</option>
                {rentalLimits.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Towing & Labor */}
            <div className="form-group">
              <label>Towing & Labor Limit</label>
              <select name="towingAndLaborLimit" className="form-input compact-select" value={formData.towingAndLaborLimit} onChange={handleChange}>
                <option value="">Select</option>
                {towingLimits.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Equipment (show for AL, GA only) */}
            <div className="form-group">
              <label>Custom Equipment Limit</label>
              <input
                type="number"
                name="customEquipmentLimit"
                className="form-input compact-select"
                value={formData.customEquipmentLimit || ""}
                min={CUSTOM_EQUIPMENT_MIN}
                max={CUSTOM_EQUIPMENT_MAX}
                onChange={handleChange}
                placeholder="Enter custom equipment value"
              />
            </div>

            {/* GAP */}
            <div className="form-group">
              <label>GAP</label>
              <select name="gap" className="form-input compact-select" value={formData.gap} onChange={handleChange}>
                <option value="">Select</option>
                {GAP_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {/* Safety Glass Coverage (show for AL, FL, GA, SC) */}
            <div className="form-group">
              <label>Safety Glass Coverage</label>
              <select
                name="safetyGlassCoverage"
                className="form-input compact-select"
                value={formData.safetyGlassCoverage}
                onChange={handleChange}
              >
                <option value="">Select</option>
                {SAFETY_GLASS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            {/* {["Alabama", "Florida", "Georgia", "Ohio", "South Carolina", "Tennessee"].includes(state) && (
              <AssignedDriverIdField value={formData.assignedDriverId} onChange={handleChange} />
            )} */}
          </div>

          <div className="tab-actions">
            <button type="button" className="btn btn-primary" onClick={onVehicleConfigurationSaveClick} disabled={saveLoading}>
              {saveLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
